import React, { useEffect, useState } from 'react';
import { useTheme } from '../../contexts/ThemeContext';
import { crossSystemService } from '../../services/crossSystemService';
import { PersonaLineageNode } from '../../types/crossSystem';
import { Persona } from '../../types/persona';
import PersonaBadge from '../common/PersonaBadge';
import { PersonaDetail as PersonaQuickView } from '../personas/PersonaDetail';
import LineageCanvas from './LineageCanvas';
import { useFabricationStore } from '../../stores/fabricationStore';
import api from '../../services/api';
import { FiGitBranch, FiGitCommit, FiUsers, FiClock, FiArrowRight, FiCopy } from 'react-icons/fi';

interface AncestorInfo {
  persona_id: string;
  name?: string;
  remix_point?: string;
  revision_at_fork?: number;
}

const LineagePanel: React.FC = () => {
  const { darkMode } = useTheme();
  const { currentPersonaId, state: forgeState } = useFabricationStore();
  const [rootNodes, setRootNodes] = useState<PersonaLineageNode[]>([]);
  const [ancestors, setAncestors] = useState<AncestorInfo[]>([]);
  const [descendants, setDescendants] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [quickViewPersona, setQuickViewPersona] = useState<Persona | null>(null);

  // Fetch persona data and show the Quick View modal
  const openQuickView = async (personaId: string) => {
    if (personaId === currentPersonaId) return;
    try {
      const resp = await api.get(`/personas/${personaId}`);
      setQuickViewPersona(resp.data);
    } catch (err) {
      console.error('Failed to load persona for quick view:', err);
    }
  };

  useEffect(() => {
    if (!currentPersonaId) return;

    const load = async () => {
      setLoading(true);
      try {
        const [ancestorData, descendantData] = await Promise.all([
          crossSystemService.getAncestors(currentPersonaId),
          crossSystemService.getDescendants(currentPersonaId),
        ]);

        setAncestors(ancestorData);
        setDescendants(descendantData);

        // Build the full heritage chain: root ancestor → ... → current → descendants
        // Walk ancestors (returned nearest-first), build a nested chain
        const descendantNodes: PersonaLineageNode[] = descendantData.map((d: any) => ({
          persona_id: d.persona_id,
          name: d.name || d.persona_id.slice(0, 8),
          tags: d.tags || [],
          revision: d.revision || 0,
          remix_point: d.remix_point,
          capability_summary: {},
          children: [],
        }));

        const currentNode: PersonaLineageNode = {
          persona_id: currentPersonaId,
          name: forgeState?.display_name || 'Current Persona',
          description: forgeState?.description,
          tags: forgeState?.tags || [],
          revision: forgeState?.revision || 0,
          capability_summary: forgeState?.capabilities || {},
          children: descendantNodes,
        };

        // Chain ancestors: each ancestor wraps the next as its child
        // ancestors = [parent, grandparent, great-grandparent, ...]
        let tree: PersonaLineageNode = currentNode;
        for (const a of ancestorData) {
          tree = {
            persona_id: a.persona_id,
            name: a.name || a.persona_id.slice(0, 8),
            tags: [],
            revision: a.revision_at_fork || 0,
            remix_point: a.remix_point,
            capability_summary: {},
            children: [tree],
          };
        }

        setRootNodes([tree]);
      } catch (err) {
        console.error('Failed to load heritage:', err);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [currentPersonaId]);

  const sectionClass = `rounded-xl border p-5 ${
    darkMode
      ? 'bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700/40 shadow-lg shadow-violet-900/10'
      : 'bg-white border-gray-200 shadow-sm'
  }`;

  const isOriginal = !forgeState?.origin_id;
  const generationDepth = ancestors.length;
  const descendantCount = descendants.length;
  const remixDate = forgeState?.remix_point
    ? new Date(forgeState.remix_point).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className={`h-24 rounded-xl ${darkMode ? 'bg-gray-800/60' : 'bg-gray-100'}`} />
        <div className={`h-64 rounded-xl ${darkMode ? 'bg-gray-800/60' : 'bg-gray-100'}`} />
      </div>
    );
  }

  return (
    <div className="space-y-6 relative">
      {/* Quick View modal — reuses the existing PersonaDetail component */}
      {quickViewPersona && (
        <PersonaQuickView
          persona={quickViewPersona}
          onClose={() => setQuickViewPersona(null)}
        />
      )}

      {/* Heritage Header */}
      <div>
        <h2 className="text-lg font-semibold bg-clip-text text-transparent bg-gradient-to-r from-violet-400 to-purple-400">
          Heritage
        </h2>
        <p className={`text-xs mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
          {isOriginal ? 'Original persona — the root of a heritage line' : `Generation ${generationDepth + 1} remix`}
        </p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className={sectionClass}>
          <div className="flex items-center gap-2 mb-1">
            <FiGitCommit className="w-3.5 h-3.5 text-violet-400" />
            <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Origin</span>
          </div>
          <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            {isOriginal ? 'Root Persona' : 'Remix'}
          </p>
          {!isOriginal && ancestors.length > 0 && (
            <p className={`text-[10px] mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
              from {ancestors[0].name || ancestors[0].persona_id.slice(0, 8)}
            </p>
          )}
        </div>

        <div className={sectionClass}>
          <div className="flex items-center gap-2 mb-1">
            <FiGitBranch className="w-3.5 h-3.5 text-purple-400" />
            <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Generation</span>
          </div>
          <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            {isOriginal ? 'Gen 1' : `Gen ${generationDepth + 1}`}
          </p>
          <p className={`text-[10px] mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {generationDepth === 0 ? 'no ancestors' : `${generationDepth} ancestor${generationDepth > 1 ? 's' : ''}`}
          </p>
        </div>

        <div className={sectionClass}>
          <div className="flex items-center gap-2 mb-1">
            <FiUsers className="w-3.5 h-3.5 text-blue-400" />
            <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Descendants</span>
          </div>
          <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            {descendantCount}
          </p>
          <p className={`text-[10px] mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {descendantCount === 0 ? 'no remixes yet' : `direct remix${descendantCount > 1 ? 'es' : ''}`}
          </p>
        </div>

        <div className={sectionClass}>
          <div className="flex items-center gap-2 mb-1">
            <FiClock className="w-3.5 h-3.5 text-cyan-400" />
            <span className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>Revision</span>
          </div>
          <p className={`text-sm font-semibold ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            rev {forgeState?.revision || 0}
          </p>
          <p className={`text-[10px] mt-0.5 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            {remixDate ? `remixed ${remixDate}` : 'original creation'}
          </p>
        </div>
      </div>

      {/* Ancestor Chain (if this is a remix) */}
      {ancestors.length > 0 && (
        <div className={sectionClass}>
          <h3 className={`text-xs font-semibold mb-3 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Ancestry Chain
          </h3>
          <div className="flex items-center gap-2 flex-wrap">
            {[...ancestors].reverse().map((a) => (
              <React.Fragment key={a.persona_id}>
                <button
                  onClick={() => openQuickView(a.persona_id)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-all group ${
                    darkMode
                      ? 'bg-gray-700/40 hover:bg-violet-900/30 border border-gray-600/30 hover:border-violet-500/40'
                      : 'bg-gray-50 hover:bg-violet-50 border border-gray-200 hover:border-violet-300'
                  }`}
                >
                  <PersonaBadge
                    personaId={a.persona_id}
                    personaName={a.name || a.persona_id.slice(0, 8)}
                    size="xs"
                    clickable={false}
                  />
                  <div className="text-left">
                    <span className={`text-xs font-medium block ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                      {(a.name || a.persona_id.slice(0, 12)).replace(/_/g, ' ')}
                    </span>
                    {a.revision_at_fork != null && (
                      <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        rev {a.revision_at_fork}
                      </span>
                    )}
                  </div>
                </button>
                <FiArrowRight className={`w-3 h-3 shrink-0 ${darkMode ? 'text-gray-600' : 'text-gray-300'}`} />
              </React.Fragment>
            ))}
            {/* Current persona at the end */}
            <div className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 ${
              darkMode
                ? 'bg-violet-900/20 border-violet-500/50'
                : 'bg-violet-50 border-violet-400/50'
            }`}>
              <PersonaBadge
                personaId={currentPersonaId || ''}
                personaName={forgeState?.display_name || 'Current'}
                size="xs"
                clickable={false}
              />
              <div className="text-left">
                <span className={`text-xs font-semibold block ${darkMode ? 'text-violet-300' : 'text-violet-600'}`}>
                  {(forgeState?.display_name || 'Current').replace(/_/g, ' ')}
                </span>
                <span className={`text-[10px] ${darkMode ? 'text-violet-400' : 'text-violet-500'}`}>
                  rev {forgeState?.revision || 0} (you are here)
                </span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Descendants List (if any) */}
      {descendants.length > 0 && (
        <div className={sectionClass}>
          <h3 className={`text-xs font-semibold mb-3 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <FiCopy className="inline w-3 h-3 mr-1 -mt-0.5" />
            Remixes ({descendants.length})
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {descendants.map((d: any) => {
              const remixAge = d.remix_point
                ? Math.floor((Date.now() - new Date(d.remix_point).getTime()) / 86400000)
                : null;
              return (
                <button
                  key={d.persona_id}
                  onClick={() => openQuickView(d.persona_id)}
                  className={`flex items-center gap-3 p-3 rounded-lg text-left transition-all group ${
                    darkMode
                      ? 'bg-gray-800/50 hover:bg-purple-900/20 border border-gray-700/30 hover:border-purple-500/30'
                      : 'bg-gray-50 hover:bg-purple-50 border border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <PersonaBadge
                    personaId={d.persona_id}
                    personaName={d.name || d.persona_id.slice(0, 8)}
                    size="sm"
                    clickable={false}
                  />
                  <div className="flex-1 min-w-0">
                    <span className={`text-sm font-medium block truncate ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
                      {(d.name || d.persona_id.slice(0, 12)).replace(/_/g, ' ')}
                    </span>
                    <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                      {remixAge != null ? (remixAge === 0 ? 'remixed today' : `${remixAge}d ago`) : 'unknown date'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Heritage Tree Visualization */}
      <div className={sectionClass}>
        <h3 className={`text-xs font-semibold mb-3 uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
          Heritage Tree
        </h3>
        <div className="h-[400px] rounded-lg overflow-hidden" style={{ minHeight: 300 }}>
          <LineageCanvas
            rootNodes={rootNodes}
            focusPersonaId={currentPersonaId || undefined}
            scope="focused"
            onNavigate={(id) => openQuickView(id)}
          />
        </div>
      </div>

      {/* Empty Heritage State */}
      {rootNodes.length === 0 && !loading && (
        <div className={`${sectionClass} text-center py-12`}>
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4 bg-gradient-to-br from-violet-500/20 to-purple-500/20">
            <FiGitBranch className="w-7 h-7 text-violet-400" />
          </div>
          <h3 className={`text-lg font-semibold mb-2 ${darkMode ? 'text-gray-200' : 'text-gray-700'}`}>
            This persona's heritage starts here
          </h3>
          <p className={`text-sm max-w-md mx-auto ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
            Every remix creates a new branch in the heritage tree. When others remix this persona,
            their creations will appear here as descendants — building a living lineage of evolved ideas.
          </p>
        </div>
      )}
    </div>
  );
};

export default LineagePanel;
